import { useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { BACKEND_URL } from "@/constants/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Users, ChevronLeft } from "lucide-react";

export default function JoinSchool() {
  const { getToken } = useAuth();
  const navigate = useNavigate();

  const [inviteCode, setInviteCode] = useState("");
  const [joining, setJoining] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const code = inviteCode.trim();
    if (!code) return;

    setJoining(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/schools/join`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ inviteCode: code }),
      });

      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || "Failed to join school");

      toast.success(`Joined ${body.school?.name || "school"} successfully!`);
      navigate(`/schools/${body.school?._id || ""}`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setJoining(false);
    }
  };

  return (
    <div className="dashboard-content">
      <Link
        to="/schools"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"
      >
        <ChevronLeft size={14} />
        My Schools
      </Link>

      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <Users size={18} className="text-primary" />
        </div>
        <div>
          <h1 className="dashboard-heading-sm">Join a School</h1>
          <p className="dashboard-subheading">
            Enter the invite code shared by your school admin.
          </p>
        </div>
      </div>

      <div className="dashboard-content-narrow">
      <div className="bg-card border border-border rounded-2xl shadow-sm">
        <form onSubmit={handleSubmit} className="px-6 py-6 space-y-5">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">
              Invite Code <span className="text-destructive/80 font-normal">*</span>
            </Label>
            <Input
              placeholder="e.g. aB3kX9mQ2z"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value)}
              required
              className="h-10 font-mono tracking-wider"
              autoComplete="off"
              spellCheck={false}
            />
            <p className="text-xs text-muted-foreground">
              Ask your school admin for the invite code.
            </p>
          </div>

          <div className="flex gap-2 pt-1">
            <Button
              type="submit"
              disabled={joining || !inviteCode.trim()}
              className="flex-1"
            >
              {joining ? "Joining..." : "Join School"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/schools")}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
      </div>
    </div>
  );
}
